import React, { useContext } from 'react';
import Button from '../Button';
import { LanguageContext } from '../LanguageContext';
import { ArchitectureIcon } from '../icons/ArchitectureIcon';
import { MediaIcon } from '../icons/MediaIcon';
import { RnDIcon } from '../icons/RnDIcon';
import { AdvisoryIcon } from '../icons/AdvisoryIcon';
import { OperationsIcon } from '../icons/OperationsIcon';


const Home: React.FC<{ openChat: (source?: string) => void }> = ({ openChat }) => {
    const { t } = useContext(LanguageContext);

    // The data for "What We Do" is the same as the main services
    const whatWeDo = t?.data?.services || [];

    const iconMap: { [key: string]: React.FC<{ className?: string }> } = {
        architecture: ArchitectureIcon,
        media: MediaIcon,
        operations: OperationsIcon,
        rd: RnDIcon,
        advisory: AdvisoryIcon,
    };

    const handleServicePathClick = (event: React.MouseEvent<HTMLAnchorElement>, serviceId: string) => {
        event.preventDefault();
        window.gtag?.('event', 'service_path_selected', { service_id: serviceId });

        const serviceDetails = document.getElementById(`service-${serviceId}`) as HTMLDetailsElement | null;
        if (!serviceDetails) return;

        serviceDetails.open = true;
        window.requestAnimationFrame(() => {
            serviceDetails.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    return (
        <section id="home" className="py-20 sm:py-32 bg-white dark:bg-black scroll-mt-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight text-gray-900 dark:text-gray-50">
                    {t?.home?.title1 || ''} <span className="block text-accent">{t?.home?.title2 || ''}</span>
                </h1>
                <p className="mt-6 max-w-3xl mx-auto text-lg sm:text-xl text-gray-600 dark:text-gray-400">
                    {t?.home?.description || ''}
                </p>
                <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button onClick={() => openChat('hero')} variant="primary">{t?.assistant?.title || 'AI Assistant'}</Button>
                    <Button href="#contact" variant="secondary">{t?.home?.buttonBookCall || 'Contact us'}</Button>
                </div>
            </div>

            <div id="what-we-do" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 sm:mt-32 scroll-mt-20">
                <div className="text-center">
                    <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-gray-900 dark:text-gray-50">{t?.home?.whatWeDoTitle || ''}</h2>
                    <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t?.home?.whatWeDoDescription || ''}</p>
                </div>
                <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                    {whatWeDo.map((item: any, index: number) => {
                        const IconComponent = item?.id ? iconMap[item.id] : null;
                        return (
                            <a
                                key={item?.id || index}
                                href={`#service-${item?.id || index}`}
                                onClick={(event) => handleServicePathClick(event, item?.id || String(index))}
                                className="group block border border-gray-200 dark:border-gray-800 p-6 rounded-xl text-center hover:shadow-lg hover:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-all duration-300"
                            >
                                <div className="flex items-center justify-center h-12 w-12 mx-auto text-gray-700 dark:text-gray-300 group-hover:text-accent transition-colors duration-300">
                                    {IconComponent && <IconComponent className="h-10 w-10" />}
                                </div>
                                <h3 className="mt-4 text-lg font-bold text-gray-900 dark:text-gray-50">{item?.pillar || ''}</h3>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{item?.description || ''}</p>
                                <svg className="mt-5 mx-auto h-5 w-5 text-accent transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M4 10h12m-5-5 5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </a>
                        );
                    })}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 sm:mt-32">
                <div className="text-center">
                    <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-gray-900 dark:text-gray-50">{t?.home?.howItWorksTitle || ''}</h2>
                    <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">{t?.home?.howItWorksDescription || ''}</p>
                </div>
                <div className="mt-12 grid gap-8 md:grid-cols-3">
                    {(t?.home?.howItWorksSteps || []).map((item: any, index: number) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-900/50 p-8 rounded-lg">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-50">{item?.title || ''}</h3>
                            <p className="mt-2 text-gray-600 dark:text-gray-400">{item?.description || ''}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 sm:mt-32">
                <div className="text-center">
                    <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-gray-900 dark:text-gray-50">{t?.home?.quickAnswers?.title || ''}</h2>
                </div>
                <div className="mt-12 max-w-4xl mx-auto grid gap-8 md:grid-cols-1">
                    {(t?.home?.quickAnswers?.qa || []).map((item: any, index: number) => (
                        <div key={index} className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-lg">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{item?.question || ''}</h3>
                            <p className="mt-2 text-gray-600 dark:text-gray-400">{item?.answer || ''}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20 sm:mt-32">
                <div className="bg-black dark:bg-gray-900 text-white p-12 rounded-lg text-center">
                    <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t?.home?.ctaTitle || ''}</h2>
                    <p className="mt-4 text-lg text-gray-300">{t?.home?.ctaDescription || ''}</p>
                    <div className="mt-8">
                        <Button onClick={() => openChat('home_cta')} variant="secondary">{t?.assistant?.title || 'AI Assistant'}</Button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Home;
